/**
 * LIVORA NATIVE CALL MIGRATION — PHASE N2A / N2A1.
 *
 * In-memory, module-scoped preparation cache. Both CallListener.js (receiver
 * incoming ringing) and OutgoingCallPage.js (caller outgoing ringing) start
 * preparation here as soon as callId is known; NativeCallRoomPage.jsx (after
 * Accept / status=ongoing) consumes whatever is already done instead of
 * re-fetching from scratch. Never persisted - cleared on call
 * completion/cancellation and expires on its own after a short TTL so a
 * stale ringing call can never leak into a later, unrelated call.
 *
 * Never touches billing, never opens the native Activity, never activates
 * camera/microphone - this only prefetches read-only Zego bootstrap data and
 * auth state/tokens.
 */

import { getFunctions, httpsCallable } from "firebase/functions";
import { auth } from "../firebase";
import { prefetchZegoBootstrap } from "./callMediaWarmup";
import { logCallStartupEvent } from "./callStartupTrace";
import {
  getNativeCallAuthState,
  prepareNativeCall as dispatchPrepareNativeCall,
  clearPreparedNativeCall as dispatchClearPreparedNativeCall,
} from "../plugins/NativeLivoraCall";

const NATIVE_SESSION_TOKEN_WAIT_MS = 5000;
// Generous relative to the 30s ringing auto-reject - a stale/abandoned
// preparation entry never survives long enough to leak into a new call.
const PREPARATION_TTL_MS = 90000;

const preparationByCallId = new Map();

function isExpired(entry) {
  return Date.now() - entry.startedAt > PREPARATION_TTL_MS;
}

async function fetchNativeCustomToken() {
  try {
    const functions = getFunctions(undefined, "us-central1");
    const createNativeCallSession = httpsCallable(functions, "createNativeCallSession");
    let finished = false;
    const outcome = await Promise.race([
      createNativeCallSession({})
        .then((res) => {
          if (finished) return null;
          finished = true;
          return { kind: "ok", res };
        })
        .catch((err) => {
          if (finished) return null;
          finished = true;
          return { kind: "error", err };
        }),
      new Promise((resolve) => {
        setTimeout(() => {
          if (finished) {
            resolve(null);
            return;
          }
          finished = true;
          resolve({ kind: "timeout" });
        }, NATIVE_SESSION_TOKEN_WAIT_MS);
      }),
    ]);
    return outcome?.kind === "ok" ? String(outcome.res?.data?.customToken || "") || null : null;
  } catch (_e) {
    return null;
  }
}

/**
 * Starts (or reuses, if already in flight/done for the same callId+uid)
 * parallel preparation of: Zego bootstrap, a cached Web ID token, and native
 * auth readiness. Never awaits one before starting another. Safe to call
 * multiple times for the same ringing call.
 */
export function beginNativeCallPreparation({ callId, uid, userName, role }) {
  const key = String(callId || "");
  if (!key || !uid) {
    return null;
  }

  const existing = preparationByCallId.get(key);
  if (existing && existing.uid === uid && !isExpired(existing)) {
    return existing;
  }
  if (existing) {
    preparationByCallId.delete(key);
  }

  logCallStartupEvent(key, "native_prepare_requested", role);

  const entry = {
    uid,
    role,
    startedAt: Date.now(),
  };

  logCallStartupEvent(key, "zego_bootstrap_start", role);
  entry.bootstrapPromise = prefetchZegoBootstrap(key, userName).then((result) => {
    logCallStartupEvent(key, "zego_bootstrap_ready", role);
    return result;
  });

  const currentUser = auth.currentUser;
  entry.idTokenPromise = currentUser
    ? currentUser
        .getIdTokenResult(false)
        .then((tokenResult) => {
          const expMs = Date.parse(String(tokenResult?.expirationTime || ""));
          return {
            token: String(tokenResult?.token || ""),
            expirationMs: Number.isFinite(expMs) ? expMs : 0,
          };
        })
        .catch(() => null)
    : Promise.resolve(null);

  // Phase N2A1 section 2/4: real, awaited native-auth readiness. Never
  // resolves "ready" merely because a request was dispatched - either an
  // immediate probe finds a live matching session (no callable, no sign-in
  // at all), or a real sign-in is attempted and this waits for FirebaseAuth's
  // actual completion.
  entry.nativeAuthPromise = (async () => {
    logCallStartupEvent(key, "native_auth_start", role);

    const probe = await getNativeCallAuthState({ expectedUid: uid });
    if (probe?.present && probe?.uidMatch) {
      logCallStartupEvent(key, "native_auth_ready", role);
      return { authReady: true, uidMatch: true, reason: "reused", customToken: null };
    }

    if (preparationByCallId.get(key) !== entry) {
      return { authReady: false, uidMatch: false, reason: "superseded", customToken: null };
    }

    const customToken = await fetchNativeCustomToken();
    if (!customToken) {
      return { authReady: false, uidMatch: false, reason: "no_custom_token", customToken: null };
    }
    if (preparationByCallId.get(key) !== entry) {
      return { authReady: false, uidMatch: false, reason: "superseded", customToken };
    }

    const result = await dispatchPrepareNativeCall({
      callId: key,
      expectedUid: uid,
      nativeFirebaseCustomToken: customToken,
      role,
    });
    if (result?.authReady) {
      logCallStartupEvent(key, "native_auth_ready", role);
    }
    return {
      authReady: result?.authReady === true,
      uidMatch: result?.uidMatch === true,
      reason: result?.reason || "unknown",
      customToken,
    };
  })();

  preparationByCallId.set(key, entry);
  return entry;
}

/** Returns the in-flight/completed preparation entry, or null if none/expired/uid mismatch. */
export function getPreparedNativeCallData(callId, uid) {
  const key = String(callId || "");
  const entry = preparationByCallId.get(key);
  if (!entry || entry.uid !== uid || isExpired(entry)) {
    return null;
  }
  return entry;
}

/**
 * Per-call cleanup only (reject / caller cancel / timeout /
 * terminal-before-accept / accept failure / open failure). Clears this
 * callId's entry both here and on the native side - never signs out a
 * matching FirebaseAuth session.
 */
export function clearNativeCallPreparation(callId) {
  const key = String(callId || "");
  if (!key) {
    return;
  }
  const entry = preparationByCallId.get(key);
  preparationByCallId.delete(key);
  if (entry?.uid) {
    void dispatchClearPreparedNativeCall({ callId: key, expectedUid: entry.uid }).catch(() => {});
  }
}

/** Real logout/account-switch only - never on a single call ending. */
export function clearAllNativeCallPreparation() {
  preparationByCallId.clear();
}
